import { useEffect, useState, useRef, useCallback } from "react";
import { useSelector } from "react-redux";
import { useQuery, useQueryClient } from "react-query";
import { ModalConfirm } from "tamtam-components";
import { toast } from "react-toastify";
import { formatPhoneNumberIntl } from "react-phone-number-input";
import InputMask from "react-input-mask";
import classNames from "classnames";
import debounce from "lodash.debounce";
import { ClickAwayListener } from "@mui/base";
import Tooltip from "@mui/material/Tooltip";

import Loader from "../../common/Loader";
import _ from "../../../i18n";
import {
  getGoPeopleGroups,
  searchGoPeopleGroups,
  createGoPeopleGroup,
  bulkAssignGroupToContacts,
  removeGroupFromContact,
  getGoPeopleContacts,
  deleteGoPeopleContact,
  getGoPeopleContactsByEmails,
} from "../../../api";
import { validateEmail } from "../../../utils";
import styles from "./Contact.module.scss";
import Pagination from "../../common/Pagination";
import { IconTrash } from "../../common/icons/IconTrash";
import IconRefresh from "../../common/icons/IconRefresh";
import IconClose from "../../common/icons/IconClose";
import IconPlus from "../../common/icons/IconPlus";
import UserRoleModal from "./UserRoleModal";
import UserArchiveModal from "./UserArchiveModal";

const ContactList = (props) => {
  const prevSearchRef = useRef();
  prevSearchRef.current = "";
  const selectAllRef = useRef(null);
  const queryClient = useQueryClient();
  const auth = useSelector((state) => state.auth);
  const fiduciary = useSelector((state) => state.folder.fiduciary);

  const [sort, setSort] = useState({
    property: "lastName",
    dir: "asc",
  });
  const [dropDownId, setDropDownId] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState({});
  const GROUP_LIMIT = 3;
  const [search, setSearch] = useState("");
  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [currentDeleteItem, setCurrentDeleteItem] = useState(false);
  const [currentArchiveMember, setCurrentArchiveMember] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectAll, setSelectAll] = useState(false);
  const [groupSearchResults, setGroupSearchResults] = useState([]);
  const [fetching, setFetching] = useState(false);
  const [saveGroup, setSaveGroup] = useState(false);
  const [savingGroup, setSavingGroup] = useState(false);
  // Groups picked to assign to the selected contacts ({ uuid, name }; uuid null = to create)
  const [selectedGroupsToAssign, setSelectedGroupsToAssign] = useState([]);
  const [removeTarget, setRemoveTarget] = useState(null);
  const [removingGroup, setRemovingGroup] = useState(false);
  const [showList, setShowList] = useState(false);
  const [showExcludeList, setShowExcludeList] = useState(false);
  const [filterGroups, setFilterGroups] = useState([]);
  const [excludeGroups, setExcludeGroups] = useState([]);
  const [SFGroups, setSFGroups] = useState([]);
  const [SEGroups, setSEGroups] = useState([]);
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [contactNl, setContactNl] = useState(false);
  const [pasteValue, setPasteValue] = useState("");
  const [showPasteBox, setShowPasteBox] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [foundContacts, setFoundContacts] = useState([]);
  const [foundResult, setFoundResult] = useState([]);
  const [totalFoundResult, setTotalFoundResult] = useState([]);
  const [notFoundContacts, setNotFoundContacts] = useState([]);
  const [dataGoPeople, setDataGoPeople] = useState([]);
  const [nbrResult, setNbrResult] = useState(0);
  const { token, goPeopleCommunityId, jwtToken } = auth;
  const LIMIT = 10;
  const [userRoleModal, setUserRoleModal] = useState(null);

  let { isLoading } = useQuery(
    [
      "listContacts",
      token,
      sort,
      currentPage,
      filterGroups,
      excludeGroups,
      contactNl,
      goPeopleCommunityId,
    ],
    async () => {
      if (token && fiduciary && search === "") {
        // contactNl was already part of the query key, so ticking the box refetched,
        // but the value was never forwarded and both requests came out identical.
        const response = await getGoPeopleContacts(
          goPeopleCommunityId,
          filterGroups.map((group) => group.uuid),
          excludeGroups.map((group) => group.uuid),
          LIMIT,
          currentPage,
          "",
          contactNl
        );

        setNbrResult(response.total_count);
        setDataGoPeople(response.data);

        return response.data;
      }
    }
  );
  let { data: groupsData } = useQuery(
    ["listGroups", token, goPeopleCommunityId],
    async () => {
      if (token && fiduciary) {
        const response = await getGoPeopleGroups(jwtToken, goPeopleCommunityId);
        setSFGroups(response.data);
        setSEGroups(response.data);
        return response.data;
      }
    }
  );

  useEffect(() => {
    if (props.refreshList) {
      if (search !== "") {
        handleSearchChange(search, currentPage);
      } else {
        queryClient.invalidateQueries("listContacts");
      }
      props.setRefreshList(false);

      if (pasteValue && showPasteBox) {
        handlePasteData(pasteValue);
      }
    }
  }, [props.refreshList]);

  useEffect(() => {
    if (prevSearchRef.current === "") {
      setCurrentPage(1);
    }
    if (search !== "") {
      handleSearchChange(search, currentPage);
    } else {
      queryClient.invalidateQueries("listContacts");
    }
    props.setRefreshList(false);

    if (pasteValue && showPasteBox) {
      handlePasteData(pasteValue);
    }
  }, [search]);

  useEffect(() => {
    if (search !== "") {
      handleSearchChange(search, currentPage);
    }
  }, [currentPage]);

  const toggleDropDown = (e, userId) => {
    e.stopPropagation();
    dropDownId === userId ? setDropDownId(null) : setDropDownId(userId);
  };

  const handleSearchChange = async (value, page) => {
    setSearch(value);
    prevSearchRef.current = value;
    if (value === "") {
      return;
    }
    setUploading(true);
    const response = await getGoPeopleContacts(
      goPeopleCommunityId,
      [],
      [],
      LIMIT,
      page,
      value
    );
    setFoundResult(response.data);
    setTotalFoundResult(response.total_count ?? 0);
    setUploading(false);
  };

  const handleDeleteAction = () => {
    if (currentDeleteItem) {
      setDeleting(true);
      deleteGoPeopleContact(goPeopleCommunityId, currentDeleteItem.uuid)
        .then((resp) => {
          if (search !== "") {
            handleSearchChange(search, currentPage);
          } else {
            queryClient.invalidateQueries("listContacts");
          }
          toast.success(_("successfully_deleted"));
          setDeleting(false);
          setIsOpenDeleteModal(false);
          props.setRefreshList(true);
          setDropDownId(null);
        })
        .catch((e) => {
          setDeleting(false);
        });
    }
  };

  const getActions = (item) => {
    if (
      auth.user?.role === "OFFICIAL" ||
      auth.user?.role === "EXTERNAL" ||
      ((auth.user?.role === "MANAGER" ||
        auth.user?.role == "FIDUCIARIES_ADMIN") &&
        item.role?.type === "LEGAL_REPRESENTATIVE")
    ) {
      return null;
    }
    if (
      "LEGAL_REPRESENTATIVE" === auth.user?.role &&
      "ADMIN" === auth.user?.role.typeStatus &&
      "LEGAL_REPRESENTATIVE" === item?.role.type &&
      auth.user.id !== item.id
    ) {
      return null;
    }

    const adminIsGroup = item.groups?.filter((i) => i.id === 535);
    if (adminIsGroup && adminIsGroup.length > 0) {
      return (
        <div className={styles.lockIcon}>
          <i className="icon-ttp-locked"></i>
        </div>
      );
    }

    return (
      <div className={styles.dropDownContainer}>
        {/* The click belongs on cellIcon, which draws the 2rem circle the user
            aims at. The inner span is only the icon glyph and settingsIcon is
            not defined in this stylesheet, so it never received a size: the hit
            area was a fraction of the visible button. */}
        <span
          className={`${styles.cellIcon} ${
            dropDownId === item.uuid && styles.active
          }`}
          onClick={(e) => toggleDropDown(e, item.uuid)}
        >
          <span
            className={`${
              dropDownId === item.uuid
                ? "icon-ttp-close"
                : "icon-ttp-three-dots"
            } ${styles.settingsIcon}`}
          />
        </span>
        <ul
          className={`${styles.menuDropdown} ${
            dropDownId === item.uuid && styles.show
          }`}
        >
          <li
            onClick={(e) => {
              toggleDropDown(e, item.uuid);
              props.setSideBar && props.setSideBar("CONTACT_FORM");
              props.setEmailValue && props.setEmailValue("");
              props.setCurrentItem(item);
              props.setIsEditingContact(true);
            }}
          >
            {_("edit")}
          </li>

          <li
            onClick={(e) => {
              toggleDropDown(e, item.uuid);
              setUserRoleModal(item);
            }}
          >
            {_("view_delegations_roles")}
          </li>

          {(!["LEGAL_REPRESENTATIVE", "MANAGER"].includes(auth.user?.role) ||
            "ADMIN" === auth.user.type) &&
            auth.user.id !== item.uuid && (
              <>
                <li
                  onClick={(e) => {
                    console.log("ssss", item);

                    setCurrentArchiveMember(item);
                    toggleDropDown(e, item.uuid);
                  }}
                >
                  Archiver
                </li>

                <li
                  onClick={() => {
                    setCurrentDeleteItem(item);
                    setIsOpenDeleteModal(true);
                  }}
                >
                  {_("delete")}
                </li>
              </>
            )}
        </ul>
      </div>
    );
  };

  const getDisplayedRows = () => {
    if (search !== "" || showPasteBox) {
      return foundResult || [];
    }
    return dataGoPeople || [];
  };

  const selectContact = (item) => {
    if (selectedIds.includes(item.uuid)) {
      setSelectedIds(selectedIds.filter((element) => element !== item.uuid));
    } else {
      setSelectedIds([...selectedIds, item.uuid]);
    }
  };

  const handleSelectAll = () => {
    const rows = getDisplayedRows();
    const allSelected =
      rows.length > 0 && rows.every((item) => selectedIds.includes(item.uuid));
    if (allSelected) {
      setSelectedIds([]);
    } else {
      setSelectedIds(rows.map((item) => item.uuid));
    }
  };

  useEffect(() => {
    const rows = getDisplayedRows();
    const allSelected =
      rows.length > 0 && rows.every((item) => selectedIds.includes(item.uuid));
    const someSelected = rows.some((item) => selectedIds.includes(item.uuid));
    setSelectAll(allSelected);
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = someSelected && !allSelected;
    }
  }, [selectedIds, dataGoPeople, foundResult, search, showPasteBox]);

  const debouncedGroupSearch = useCallback(
    debounce(async (term, jwt, communityId) => {
      try {
        const res = await searchGoPeopleGroups(jwt, communityId, term);
        const results = res?.data || [];
        setGroupSearchResults(results);
        setShowCreateGroup(
          !results.some((g) => g.name?.toLowerCase() === term.toLowerCase())
        );
      } catch (error) {
        setGroupSearchResults([]);
        setShowCreateGroup(true);
      } finally {
        setFetching(false);
      }
    }, 300),
    []
  );

  const MIN_GROUP_SEARCH_LENGTH = 3;
  const handleGroupSearch = (name) => {
    const term = (name || "").trim();
    if (term.length < MIN_GROUP_SEARCH_LENGTH) {
      debouncedGroupSearch.cancel();
      setGroupSearchResults([]);
      setShowCreateGroup(false);
      setFetching(false);
      return;
    }
    setFetching(true);
    debouncedGroupSearch(term, jwtToken, goPeopleCommunityId);
  };

  const isGroupSelected = (group) =>
    selectedGroupsToAssign.some((g) =>
      group.uuid
        ? g.uuid === group.uuid
        : !g.uuid && g.name?.toLowerCase() === group.name?.toLowerCase()
    );

  // Add a group to the "to assign" list. The search stays open; the picked
  // group leaves the dropdown (filtered out below) while the others remain.
  const addGroupToken = (group) => {
    if (!isGroupSelected(group)) {
      setSelectedGroupsToAssign([
        ...selectedGroupsToAssign,
        { uuid: group.uuid || null, name: group.name },
      ]);
    }
  };

  const removeGroupToken = (group) => {
    setSelectedGroupsToAssign(
      selectedGroupsToAssign.filter((g) =>
        group.uuid ? g.uuid !== group.uuid : g.name !== group.name
      )
    );
  };

  const handleKeyPress = (e) => {
    if (e.key !== "Enter") return;
    const term = e.target.value.trim();
    if (term.length < MIN_GROUP_SEARCH_LENGTH) return;
    const exact = groupSearchResults.find(
      (group) => group.name?.toLowerCase() === term.toLowerCase()
    );
    addGroupToken(exact || { uuid: null, name: term });
  };

  // Resolve a group to its ref, creating it first when it doesn't exist yet
  const resolveGroupRef = async (group) => {
    if (group.uuid) return group.uuid;
    const created = await createGoPeopleGroup(
      jwtToken,
      goPeopleCommunityId,
      group.name
    );
    let ref = created?.data?.uuid || created?.uuid || null;
    if (!ref) {
      const refreshed = await getGoPeopleGroups(jwtToken, goPeopleCommunityId);
      ref =
        (refreshed?.data || []).find(
          (g) => g.name?.toLowerCase() === group.name.trim().toLowerCase()
        )?.uuid || null;
    }
    return ref;
  };

  const handleApplyGroups = async () => {
    if (selectedIds.length === 0 || selectedGroupsToAssign.length === 0) {
      return;
    }
    setSavingGroup(true);
    try {
      let failedCount = 0;
      for (const group of selectedGroupsToAssign) {
        const groupRef = await resolveGroupRef(group);
        if (!groupRef) {
          failedCount += selectedIds.length;
          continue;
        }
        const { failed } = await bulkAssignGroupToContacts(
          jwtToken,
          goPeopleCommunityId,
          selectedIds,
          groupRef
        );
        failedCount += failed.length;
      }

      if (failedCount === 0) {
        toast.success(_("group_assigned_success"));
      } else {
        toast.warning(_("group_assigned_partial"));
      }

      queryClient.invalidateQueries("listGroups");
      queryClient.invalidateQueries("listContacts");
      reset();
    } catch (error) {
      toast.error(_("error_try_again"));
    } finally {
      setSavingGroup(false);
    }
  };

  const reset = () => {
    setGroupSearchResults([]);
    setSelectedGroupsToAssign([]);
    setSaveGroup(false);
    setSelectedIds([]);
    setSelectAll(false);
    setFetching(false);
    setSearchValue("");
    setShowCreateGroup(false);
    props.setRefreshList(true);
  };

  const exitSelectionMode = () => {
    setSelectionMode(false);
    setSelectedIds([]);
    setSelectAll(false);
    setSearchValue("");
    setGroupSearchResults([]);
    setShowCreateGroup(false);
    setSelectedGroupsToAssign([]);
  };

  const handleRemoveGroupFromContact = async () => {
    if (!removeTarget) return;
    setRemovingGroup(true);
    try {
      await removeGroupFromContact(
        jwtToken,
        goPeopleCommunityId,
        removeTarget.contactUuid,
        removeTarget.groupUuid
      );
      toast.success(_("group_removed_success"));
      queryClient.invalidateQueries("listContacts");
      props.setRefreshList(true);
    } catch (error) {
      toast.error(_("group_removed_error"));
    } finally {
      setRemovingGroup(false);
      setRemoveTarget(null);
    }
  };

  // Split a pasted blob on the usual separators, then trim and drop empties:
  // exact email match needs clean values.
  const splitPastedValues = (data) => {
    let values = [];
    if (data.includes(";")) {
      values = data.split(";");
    } else if (data.includes(",")) {
      values = data.split(",");
    } else if (data.includes("\t")) {
      values = data.split("\t");
    } else {
      values = data.split(/\r\n|\r|\n/);
    }

    return values.map((value) => value.trim()).filter((value) => value !== "");
  };

  const handleSearchPaste = async (e) => {
    const data = e.clipboardData.getData("Text");

    // The paste box resolves contacts by exact email match, so it must only
    // kick in for actual emails. Anything else (a name, a partial word) is
    // left to the regular search input, which does a partial match.
    const hasEmail = splitPastedValues(data).some((value) =>
      validateEmail(value)
    );
    if (!hasEmail) {
      return;
    }

    e.preventDefault();
    setPasteValue(data);
    handlePasteData(data);
  };

  const handlePasteData = async (data) => {
    const allEmails = splitPastedValues(data);

    if (allEmails.length === 0) {
      return null;
    }

    setShowPasteBox(true);
    setUploading(true);

    try {
      const response = await getGoPeopleContactsByEmails(
        goPeopleCommunityId,
        allEmails
      );
      const contactsFound = response.data || [];
      // All emails carried by the matched contacts (shown as "found" chips).
      const emailContacts = contactsFound.reduce(
        (acc, contact) => acc.concat(contact.emails || []),
        []
      );
      // Pasted emails that matched no contact (case-insensitive comparison).
      const foundLower = new Set(
        emailContacts.map((email) => email.toLowerCase())
      );
      const contactsNotFound = allEmails.filter(
        (email) => !foundLower.has(email.toLowerCase())
      );

      // Accumulate across successive pastes (dedup) instead of replacing, so
      // pasting more emails adds to the lists rather than wiping the previous ones.
      setFoundResult((prev) => {
        const seen = new Set(prev.map((contact) => contact.uuid));
        return [...prev, ...contactsFound.filter((c) => !seen.has(c.uuid))];
      });
      setFoundContacts((prev) => {
        const seen = new Set(prev.map((email) => email.toLowerCase()));
        return [
          ...prev,
          ...emailContacts.filter((email) => !seen.has(email.toLowerCase())),
        ];
      });
      setNotFoundContacts((prev) => {
        // Drop any previously not-found email that is now found, then add the rest.
        const stillNotFound = prev.filter(
          (email) => !foundLower.has(email.toLowerCase())
        );
        const seen = new Set(stillNotFound.map((email) => email.toLowerCase()));
        return [
          ...stillNotFound,
          ...contactsNotFound.filter((email) => !seen.has(email.toLowerCase())),
        ];
      });
      setUploading(false);
    } catch (e) {
      setUploading(false);
    }
  };

  const handleAddContact = (email) => {
    props.setCurrentItem(null);
    props.setEmailValue(email);
    props.setSideBar("CONTACT_FORM");
  };

  const handleDeleteContact = (email) => {
    const tab = foundContacts.filter((i) => i !== email);
    setFoundContacts(tab);

    const t = foundResult.filter(
      (contact) =>
        !(contact.emails || []).some(
          (e) => e.toLowerCase() === email.toLowerCase()
        )
    );
    setFoundResult(t);
  };

  const handleCopy = async (tab) => {
    let values = "";
    if (pasteValue.includes(";")) {
      values = tab.join(";");
    } else if (pasteValue.includes(",")) {
      values = tab.join(",");
    } else if (pasteValue.includes("\t")) {
      values = tab.join("\t");
    } else {
      values = tab.join("\n");
    }

    try {
      await navigator.clipboard?.writeText(values);
      toast.success("Données copiées");
    } catch (error) {
      console.error("Unable to copy to clipboard:", error);
    }
  };

  const renderList = (data) => {
    return data.map((item) => (
      <tr
        key={item.uuid}
        className={
          selectionMode && selectedIds.includes(item.uuid)
            ? styles.rowSelected
            : ""
        }
      >
        {selectionMode && (
          <td className={styles.checkboxCell}>
            <input
              type="checkbox"
              checked={selectedIds.includes(item.uuid)}
              onChange={() => selectContact(item)}
            />
          </td>
        )}
        <td>{getActions(item)}</td>
        <td>
          {`${item.last_name ? item.last_name : ""} ${
            item.first_name ? item.first_name : ""
          }`}
          {((item.last_name !== undefined && item.last_name !== "") ||
            (item.first_name !== undefined && item.first_name !== "")) && (
            <br />
          )}
          {item.emails ? item.emails[0] : ""}
        </td>
        <td>
          {item.contact_groups && (() => {
            const isExpanded = expandedGroups[item.uuid];
            const visible = isExpanded
              ? item.contact_groups
              : item.contact_groups.slice(0, GROUP_LIMIT);
            const remaining = item.contact_groups.length - GROUP_LIMIT;
            return (
              <div className={styles.groupCell}>
                {visible.map((element) => (
                  <span key={element.uuid} className={styles.groupName}>
                    {element.name}
                    <span
                      className={styles.groupRemove}
                      title={_("remove_group_title")}
                      onClick={(e) => {
                        e.stopPropagation();
                        setRemoveTarget({
                          contactUuid: item.uuid,
                          groupUuid: element.uuid,
                          groupName: element.name,
                        });
                      }}
                    >
                      <IconTrash size={11} />
                    </span>
                  </span>
                ))}
                {!isExpanded && remaining > 0 && (
                  <span
                    className={styles.groupMore}
                    onClick={() =>
                      setExpandedGroups((prev) => ({ ...prev, [item.uuid]: true }))
                    }
                  >
                    <strong>+{remaining}</strong> {_("show_more")}
                  </span>
                )}
                {isExpanded && (
                  <span
                    className={styles.groupMore}
                    onClick={() =>
                      setExpandedGroups((prev) => ({ ...prev, [item.uuid]: false }))
                    }
                  >
                    {_("show_less")}
                  </span>
                )}
              </div>
            );
          })()}
        </td>
        <td className={styles.nowrap}>
          {item.phones && item.phones.length > 0
            ? formatPhoneNumberIntl(item.phones[0])
            : "-"}
        </td>
        <td>{item.genre ? _(item.genre.toLowerCase()) : "-"}</td>
        <td>
          {item.language
            ? item.language.charAt(0).toUpperCase() + item.language.slice(1)
            : "-"}
        </td>
        <td>
          {item?.contactClient?.details ? (
            <>
              {item.contactClient.details.name &&
                item.contactClient.details.name}
              <br />
              {item.contactClient.details.uen && (
                <div className={styles.uen}>
                  BE
                  <InputMask
                    readOnly={true}
                    mask="9999.999.999"
                    value={item.contactClient.details.uen}
                  />
                </div>
              )}
            </>
          ) : (
            "-"
          )}
        </td>
        <td>
          {item?.contactClient?.details && item.contactClient.details.fonction
            ? item.contactClient.details.fonction
            : "-"}
        </td>
      </tr>
    ));
  };

  return (
    <>
      <div className={styles.listContainer}>
        <h3>
          {_("contacts_list")}{" "}
          {search !== "" ? (
            <span>({totalFoundResult})</span>
          ) : filterGroups.length || excludeGroups.length ? (
            <span>({nbrResult})</span>
          ) : showPasteBox && !uploading ? (
            <span>({foundContacts.length})</span>
          ) : (
            !isLoading && nbrResult && <span>({nbrResult})</span>
          )}
        </h3>
        <div className={styles.inputSearch}>
          <span className={styles.inputSearch_icon}>
            <i className="icon-ttp-magnifier"></i>
          </span>
          <input
            type="text"
            id="search-go-contact"
            autoComplete="off"
            placeholder="Recherche ..."
            onChange={(e) => handleSearchChange(e.target.value, 1)}
            onPaste={handleSearchPaste}
          />
          {search !== "" && (
            <span
              className={styles.inputSearch_close}
              onClick={() => {
                setSearch("");
                prevSearchRef.current = "";
                document.getElementById("search-go-contact").value = "";
              }}
            >
              <i className="icon-ttp-close"></i>
            </span>
          )}
        </div>
      </div>
      {props.page && props.page === "SETTING" && (
        <ul className={styles.tabForms}>
          <li
            onClick={() => {
              props.setCurrentItem(null);
              props.setEmailValue && props.setEmailValue("");
              props.setSideBar("CONTACT_FORM");
            }}
            className={props.sideBar === "CONTACT_FORM" ? styles.active : ""}
          >
            Ajout Manuel
          </li>
          <li
            onClick={() => {
              props.setCurrentItem(null);
              props.setSideBar("CONTACT_IMPORT");
            }}
            className={props.sideBar === "CONTACT_IMPORT" ? styles.active : ""}
          >
            Import (.xls)
          </li>
        </ul>
      )}

      {showPasteBox && (
        <div className={styles.pasteBox}>
          <span
            className={styles.pasteBox_close}
            onClick={() => {
              setShowPasteBox(false);
              setPasteValue("");
              setFoundResult([]);
              setFoundContacts([]);
              setNotFoundContacts([]);
            }}
          >
            <IconClose size="15" />
          </span>
          {uploading ? (
            <div className={styles.load}>
              <span className={styles.spinner}></span>
            </div>
          ) : (
            <div className={styles.paste_content}>
              <div className={styles.paste_left}>
                <label>
                  Trouvé ({foundContacts.length})
                  {foundContacts.length > 0 && (
                    <span
                      className={styles.paste_copy}
                      onClick={() => handleCopy(foundContacts)}
                    >
                      <i className="icon-ttp-copy"></i>
                    </span>
                  )}
                </label>

                <div className={styles.paste_items}>
                  {foundContacts.map((item, idx) => (
                    <span className={styles.paste_item} key={`f${idx}`}>
                      <span
                        className={styles.paste_add}
                        onClick={() => handleDeleteContact(item)}
                      >
                        <IconClose size="8" />
                      </span>
                      {item}
                    </span>
                  ))}
                </div>
              </div>
              <div className={styles.paste_right}>
                <label>
                  Non trouvé ({notFoundContacts.length}){" "}
                  {notFoundContacts.length > 0 && (
                    <span
                      className={styles.paste_copy}
                      onClick={() => handleCopy(notFoundContacts)}
                    >
                      <i className="icon-ttp-copy"></i>
                    </span>
                  )}
                </label>

                <div className={styles.paste_items}>
                  {notFoundContacts.map((item, idx) => (
                    <span className={styles.paste_item} key={`nf${idx}`}>
                      <span
                        className={styles.paste_add}
                        onClick={() => handleAddContact(item)}
                      >
                        <IconPlus />
                      </span>
                      {item}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
      <div className={styles.groupFilter}>
        <div className={styles.select}>
          <button
            className={showList ? styles.active_btn : ""}
            onClick={() => {
              setSFGroups(groupsData);
              setShowList(!showList);
            }}
          >
            {_("group_filter")}
            <svg
              width="10"
              height="6"
              viewBox="0 0 10 6"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M5.77771 5.60814C5.37747 6.10332 4.62253 6.10332 4.22229 5.60814L1.12024 1.77036C0.591671 1.11642 1.05711 0.141741 1.89796 0.141741L8.10204 0.141741C8.94289 0.141742 9.40833 1.11642 8.87976 1.77036L5.77771 5.60814Z"
                fill="#6D7F92"
              />
            </svg>
          </button>
          <button
            className={styles.refresh}
            onClick={() => setFilterGroups([])}
          >
            <IconRefresh />
          </button>
          {showList && (
            <ClickAwayListener onClickAway={() => setShowList(false)}>
              <div className={styles.list}>
                <input
                  type="text"
                  placeholder={_("search_group")}
                  onChange={(e) => {
                    if (e.target.value.length > 0) {
                      setSFGroups(
                        groupsData.filter((el) =>
                          el.name
                            .toLowerCase()
                            .startsWith(e.target.value.toLocaleLowerCase())
                        )
                      );
                    } else {
                      setSFGroups(groupsData);
                    }
                  }}
                />
                <ul>
                  {SFGroups &&
                    SFGroups.map((item) => (
                      <li
                        className={classNames(
                          filterGroups.includes(item) ? styles.selected : "",
                          excludeGroups.includes(item) ? styles.disabled : ""
                        )}
                        key={item.uuid}
                        onClick={() => {
                          if (!excludeGroups.includes(item)) {
                            if (filterGroups.includes(item)) {
                              setFilterGroups(
                                filterGroups.filter(
                                  (el) => el.uuid !== item.uuid
                                )
                              );
                            } else {
                              setFilterGroups([...filterGroups, item]);
                            }
                          }
                        }}
                      >
                        {item.name}
                      </li>
                    ))}
                </ul>
              </div>
            </ClickAwayListener>
          )}
        </div>
        <div className={styles.select}>
          <button
            className={showExcludeList ? styles.active_btn : ""}
            onClick={() => {
              setSEGroups(groupsData);
              setShowExcludeList(!showExcludeList);
            }}
          >
            {_("group_exclude")}
            <svg
              width="10"
              height="6"
              viewBox="0 0 10 6"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M5.77771 5.60814C5.37747 6.10332 4.62253 6.10332 4.22229 5.60814L1.12024 1.77036C0.591671 1.11642 1.05711 0.141741 1.89796 0.141741L8.10204 0.141741C8.94289 0.141742 9.40833 1.11642 8.87976 1.77036L5.77771 5.60814Z"
                fill="#6D7F92"
              />
            </svg>
          </button>
          <button
            className={styles.refresh}
            onClick={() => setExcludeGroups([])}
          >
            <IconRefresh />
          </button>
          {showExcludeList && (
            <ClickAwayListener onClickAway={() => setShowExcludeList(false)}>
              <div className={styles.list}>
                <input
                  type="text"
                  placeholder={_("search_group")}
                  onChange={(e) => {
                    if (e.target.value.length > 0) {
                      setSEGroups(
                        groupsData.filter((el) =>
                          el.name
                            .toLowerCase()
                            .startsWith(e.target.value.toLocaleLowerCase())
                        )
                      );
                    } else {
                      setSEGroups(groupsData);
                    }
                  }}
                />
                <ul>
                  {SEGroups &&
                    SEGroups.map((item) => (
                      <li
                        className={classNames(
                          excludeGroups.includes(item) ? styles.selected : "",
                          filterGroups.includes(item) ? styles.disabled : ""
                        )}
                        key={item.uuid}
                        onClick={() => {
                          if (!filterGroups.includes(item)) {
                            if (excludeGroups.includes(item)) {
                              setExcludeGroups(
                                excludeGroups.filter(
                                  (el) => el.uuid !== item.uuid
                                )
                              );
                            } else {
                              setExcludeGroups([...excludeGroups, item]);
                            }
                          }
                        }}
                      >
                        {item.name}
                      </li>
                    ))}
                </ul>
              </div>
            </ClickAwayListener>
          )}
        </div>
        <Tooltip
          title={_("manage_groups")}
          arrow
          placement="top"
          enterDelay={100}
          enterTouchDelay={0}
        >
          <button
            type="button"
            className={classNames(
              styles.selectBtn,
              selectionMode ? styles.selectBtn_active : ""
            )}
            aria-label={_("manage_groups")}
            onClick={() =>
              selectionMode ? exitSelectionMode() : setSelectionMode(true)
            }
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.7"
              strokeLinecap="round"
              strokeLinejoin="round"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
              <line x1="7" y1="7" x2="7.01" y2="7" />
            </svg>
          </button>
        </Tooltip>
        <div className={styles.wrapCheckbox}>
          <input
            type="checkbox"
            checked={contactNl}
            onChange={() => {
              setCurrentPage(1);
              setContactNl(!contactNl);
            }}
          />
          <span>Contact NL</span>
        </div>
      </div>
      {filterGroups.length > 0 && (
        <div className={styles.groupList}>
          <span>Les groupes filtrés</span>
          <ul>
            {filterGroups.map((item) => (
              <li key={item.id} className={styles.groupName}>
                {item.name}
                <div
                  onClick={() =>
                    setFilterGroups(
                      filterGroups.filter((el) => el.uuid !== item.uuid)
                    )
                  }
                >
                  <IconClose size={10} />
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
      {excludeGroups.length > 0 && (
        <div className={styles.groupList}>
          <span>Les groupes exclus</span>
          <ul>
            {excludeGroups.map((item) => (
              <li key={item.id} className={styles.groupName}>
                {item.name}
                <div
                  onClick={() =>
                    setExcludeGroups(
                      excludeGroups.filter((el) => el.uuid !== item.uuid)
                    )
                  }
                >
                  <IconClose size={10} />
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
      {selectionMode && (
        <div className={styles.selectionBar}>
          <div className={styles.selectionBar_count}>
            <input
              ref={selectAllRef}
              type="checkbox"
              checked={selectAll}
              onChange={() => handleSelectAll()}
            />
            <span>
              {selectedIds.length} {_("contacts_selected")}
            </span>
          </div>

          {selectedIds.length > 0 && (
            <div className={styles.assignArea}>
              <div className={styles.inputGroup}>
                {selectedGroupsToAssign.map((group) => (
                  <span
                    key={group.uuid || group.name}
                    className={styles.tokenChip}
                  >
                    {group.name}
                    <span
                      className={styles.tokenChip_remove}
                      onClick={() => removeGroupToken(group)}
                    >
                      <IconClose size={9} />
                    </span>
                  </span>
                ))}
                <input
                  type="text"
                  placeholder={_("search_assign_group")}
                  onChange={(e) => {
                    setSearchValue(e.target.value);
                    handleGroupSearch(e.target.value);
                  }}
                  onKeyDown={(e) => handleKeyPress(e)}
                  value={searchValue}
                />
                {searchValue &&
                  (() => {
                    const visibleResults = groupSearchResults.filter(
                      (group) => !isGroupSelected(group)
                    );
                    const showCreate =
                      showCreateGroup &&
                      !selectedGroupsToAssign.some(
                        (g) =>
                          g.name?.toLowerCase() ===
                          searchValue.trim().toLowerCase()
                      );
                    if (!fetching && visibleResults.length === 0 && !showCreate) {
                      return null;
                    }
                    return (
                      <ul className={styles.searchResults}>
                        {fetching && (
                          <li className={styles.center}>
                            <Loader
                              style={{ height: "10px" }}
                              color={"#B2BCC6"}
                            />
                          </li>
                        )}
                        {!fetching &&
                          visibleResults.map((item) => (
                            <li
                              key={item.uuid}
                              className={styles.assignRow}
                              onClick={() => addGroupToken(item)}
                            >
                              <span>{item.name}</span>
                            </li>
                          ))}
                        {!fetching && showCreate && (
                          <li
                            className={styles.createGroup}
                            onClick={() =>
                              addGroupToken({ uuid: null, name: searchValue })
                            }
                          >
                            + {_("create_new_group")}
                          </li>
                        )}
                      </ul>
                    );
                  })()}
              </div>
              {selectedGroupsToAssign.length > 0 && (
                <button
                  type="button"
                  className={styles.applyBtn}
                  onClick={() => setSaveGroup(true)}
                >
                  {_("apply")}
                </button>
              )}
            </div>
          )}

          <span
            className={styles.selectionBar_close}
            onClick={() => exitSelectionMode()}
          >
            <IconClose size={14} />
          </span>
        </div>
      )}

      <div className={styles.tableContainer}>
        <table className={`${styles.table} unstriped`}>
          <thead>
            <tr>
              {selectionMode && <th className={styles.checkboxCell}></th>}
              <th></th>
              <th width="30%" className={styles.nowrap}>
                <span>{_("lastname") + " / " + _("firstname")}</span>
                <span className={styles.sortBox}>
                  <i
                    className={`icon-ttp-arrow-down ${
                      sort.property === "lastName" &&
                      sort.dir === "asc" &&
                      styles.iconActive
                    }`}
                    onClick={() =>
                      setSort({ property: "lastName", dir: "asc" })
                    }
                  ></i>
                  <i
                    className={`icon-ttp-arrow-up ${
                      sort.property === "lastName" &&
                      sort.dir === "desc" &&
                      styles.iconActive
                    }`}
                    onClick={() =>
                      setSort({ property: "lastName", dir: "desc" })
                    }
                  ></i>
                </span>
              </th>
              <th width="40%">{_("groups")}</th>
              <th width="25%">{_("phone_number")}</th>
              <th width="25%">{_("gender")}</th>
              <th>{_("language")}</th>
              <th>Entreprise</th>
              <th>Fonction</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={9} className={styles.centerCell}>
                  <Loader
                    style={{
                      height: "10px",
                    }}
                    color={"#B2BCC6"}
                  />
                </td>
              </tr>
            ) : search !== "" ? (
              renderList(foundResult)
            ) : showPasteBox ? (
              renderList(foundResult)
            ) : dataGoPeople && dataGoPeople.length > 0 ? (
              renderList(dataGoPeople)
            ) : (
              <tr>
                <td colSpan={9} className={styles.centerCell}>
                  Liste vide (aucun contact n'a été ajouté)
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {!showPasteBox && (nbrResult || totalFoundResult) ? (
        <Pagination
          className="pagination-bar"
          currentPage={currentPage}
          totalCount={search !== "" ? totalFoundResult : nbrResult}
          pageSize={LIMIT}
          onPageChange={(page) => setCurrentPage(page)}
        />
      ) : (
        ""
      )}
      <ModalConfirm
        type="delete"
        isOpen={isOpenDeleteModal}
        onCancel={() => {
          setIsOpenDeleteModal(false);
        }}
        onConfirm={() => handleDeleteAction()}
        inProcess={deleting}
        title={_("delete_confirm")}
        text={_("text_delete_collaborator")}
        labelNo={_("no")}
        labelYes={_("yes")}
        labelError={_("error")}
      />
      <ModalConfirm
        type="add"
        isOpen={saveGroup}
        onCancel={() => setSaveGroup(false)}
        onConfirm={() => handleApplyGroups()}
        inProcess={savingGroup}
        title={_("assign_group_title")}
        text={`« ${selectedGroupsToAssign
          .map((group) => group.name)
          .join(", ")} » ${_("will_be_assigned_to")} ${
          selectedIds.length
        } ${_("contacts_word")}`}
        labelNo={_("no")}
        labelYes={_("yes")}
        labelError={_("error")}
      />
      <ModalConfirm
        type="delete"
        isOpen={removeTarget !== null}
        onCancel={() => setRemoveTarget(null)}
        onConfirm={() => handleRemoveGroupFromContact()}
        inProcess={removingGroup}
        title={_("remove_group_title")}
        text={`« ${removeTarget?.groupName || ""} » ${_(
          "will_be_removed_from_contact"
        )}`}
        labelNo={_("no")}
        labelYes={_("yes")}
        labelError={_("error")}
      />

      {userRoleModal && (
        <UserRoleModal
          modalOpen={userRoleModal ? true : false}
          closeModal={() => {
            setUserRoleModal(null);
          }}
          firstName={userRoleModal.firstname || userRoleModal.first_name || ""}
          lastName={userRoleModal.lastname || userRoleModal.last_name || ""}
          email={
            userRoleModal.mainEmail ||
            (userRoleModal.emails && userRoleModal.emails[0]) ||
            ""
          }
          userId={userRoleModal.ttp_user_id}
        />
      )}

      {currentArchiveMember && (
        <UserArchiveModal
          currentArchiveMember={currentArchiveMember}
          modalOpen={currentArchiveMember ? true : false}
          closeModal={() => {
            setCurrentArchiveMember(null);
          }}
          onAfterSave={() => {
            setCurrentArchiveMember(null);
            props.setRefreshList(true);
            setDropDownId(null);
          }}
        />
      )}
    </>
  );
};

export default ContactList;
